import React, { useState, useMemo, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { ReactComponent as Dropdown } from "../../../../assets/images/dropdown-arrow.svg";
import { ListCoRoleName } from "../../../../store/features/ListCoUserByRole";
import { updateTicket } from "../../../../store/features/updateTicket";
import { addTicketTagById } from "../../../../store/features/addTicketTagById";
import { fetchTags } from "../../../../store/features/tagsApi";
import { Autocomplete, TextField, Chip, Toolbar, FormControl, Select, MenuItem, Button, Avatar, Box, Divider, InputAdornment } from "@mui/material";
import { colorCodesForAvatar } from "../../../../constants/index";
import { ReactComponent as Urgent } from "../../../../assets/images/TicketImages/urgent.svg";
import { ReactComponent as High } from "../../../../assets/images/TicketImages/high.svg";
import { ReactComponent as Medium } from "../../../../assets/images/TicketImages/medium.svg";
import { ReactComponent as Low } from "../../../../assets/images/TicketImages/low.svg";
import { ReactComponent as ChatTag } from '../../../../assets/images/ChatImages/chattag.svg';
import { DatePicker, Space } from "antd";
import "./style.css";
import { ReactComponent as SearchIcon } from '../../../../assets/images/TicketImages/search.svg';
import { SearchOffOutlined } from "@mui/icons-material";
import TeamMemberSelect from "./TeamMemberSelect";
import TicketTagInput from "../CreateTicket/TicketTagInput";
import { useSearchParams } from "react-router-dom";
import { getAllTickets, buildTicketListParams } from "../../../../store/features/getAllTickets";

/** Max tags per ticket (API + UI). Filter picker cannot exceed this either. */
const MAX_TICKET_TAGS = 5;

/** New tag ids from filter order, excluding ones already on the ticket (valid ObjectIds only). */
function getNewTagIdsFromFilter(ticket, tagsForApply, isMongoObjectId) {
  const existing = new Set((ticket.tags || []).map((t) => String(t._id || t)));
  const ordered = [];
  for (const t of tagsForApply) {
    const id = String(t._id || t);
    if (!isMongoObjectId(id) || existing.has(id)) continue;
    if (!ordered.includes(id)) ordered.push(id);
  }
  return ordered;
}

const TicketFilterToolBar = (props) => {
  const priorities = [
    { value: "urgent", label: "Urgent Priority" },
    { value: "high", label: "High Priority" },
    { value: "medium", label: "Medium Priority" },
    { value: "low", label: "Low Priority" },
  ];

  const {
    numSelected,
    t,
    selectedTickets,
    setLocalTickets,
    setSelected,
    tickets,
    activeTab,
  } = props;

  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [dateRange, setDateRange] = useState([null, null]);
  const [isApplying, setIsApplying] = useState(false);
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [assignedAgent, setAssignedAgent] = useState("");
  const [tags, setTags] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const autocompleteRef = useRef(null);
  const [searchMemberTerm, setSearchMemberTerm] = useState('');

  const filteredMembers = teamMembers.filter(member =>
    member.full_name.toLowerCase().includes(searchMemberTerm.toLowerCase()) ||
    (member.email && member.email.toLowerCase().includes(searchMemberTerm.toLowerCase()))
  );

  const businessId = useSelector((state) => state.loginToken?.response?.user?.business_id);
  const { list: availableTags = [], loading } = useSelector((state) => state.tags);

  useEffect(() => {
    if (businessId) {
      dispatch(fetchTags({ businessId, type: "conversation" }));
    }
  }, [dispatch, businessId]);

  const defaultTags = [
    "refund",
    "customer-support",
    "technical",
    "billing",
    "feature-request",
  ];

  useEffect(() => {
    const fetchTeamMembers = async () => {
      const response = await dispatch(ListCoRoleName({ name: "agent,admin,admin_with_billing" }));
      if (response.payload?.status === 200) {
        setTeamMembers(response.payload.data);
      }
    };
    fetchTeamMembers();
  }, [dispatch]);

  useEffect(() => {
    if (tags.length > 0 && autocompleteRef.current) {
      setTimeout(() => {
        const inputElement = autocompleteRef.current.querySelector('.MuiOutlinedInput-root');
        if (inputElement) {
          inputElement.scrollLeft = inputElement.scrollWidth;
        }
      }, 100);
    }
  }, [tags]);

  const handleTagChange = (event, newValue) => {
    const processedTags = newValue.map((tag) => {
      if (typeof tag === "string") {
        const existingTag = availableTags.find((t) => t.name === tag);
        if (existingTag) {
          return existingTag;
        }
        return { name: tag, _id: tag };
      }
      return tag;
    });
    if (processedTags.length > MAX_TICKET_TAGS) {
      toast.error(`You can only add up to ${MAX_TICKET_TAGS} tags.`);
    }
    setTags(processedTags.slice(0, MAX_TICKET_TAGS));
  };

  const isMongoObjectId = (id) =>
    typeof id === "string" && /^[a-fA-F0-9]{24}$/.test(id);

  const handleApply = async () => {
    setIsApplying(true);
    try {
      const tagsForApply = tags.slice(0, MAX_TICKET_TAGS);
      let anyTagSkippedForTicketCap = false;
      for (const ticketId of selectedTickets) {
        const t = tickets.find((x) => x._id === ticketId);
        if (!t) continue;
        const existingSize = new Set(
          (t.tags || []).map((tag) => String(tag._id || tag))
        ).size;
        const newIds = getNewTagIdsFromFilter(t, tagsForApply, isMongoObjectId);
        const room = Math.max(0, MAX_TICKET_TAGS - existingSize);
        if (newIds.length > room) {
          anyTagSkippedForTicketCap = true;
          break;
        }
      }
      if (anyTagSkippedForTicketCap) {
        toast.info(
          `Some tags were not added: each ticket can have at most ${MAX_TICKET_TAGS} tags.`
        );
        return;
      }
      let filteredTickets = tickets;
      const updates = {};
      if (status) updates.status = status;
      if (priority) updates.priority = priority;

      let assigneeData = null;
      if (assignedAgent) {
        const selectedAgent = teamMembers.find(
          (member) => member._id === assignedAgent
        );

        if (selectedAgent) {
          assigneeData = {
            assignee: {
              _id: assignedAgent,
              full_name: selectedAgent.full_name,
              agent_image: selectedAgent.agent_image || null,
            },
          };
          updates.assignee = assigneeData.assignee;
        }
      }
      if (dateRange[0] && dateRange[1]) {
        const startDate = new Date(dateRange[0]);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(dateRange[1]);
        endDate.setHours(23, 59, 59, 999);
        filteredTickets = filteredTickets.filter(ticket => {
          const ticketDate = new Date(ticket.updated_at);
          return ticketDate >= startDate && ticketDate <= endDate;
        });
      }

      setLocalTickets(filteredTickets);
      setLocalTickets((prevTickets) =>
        prevTickets.map((ticket) => {
          if (selectedTickets.includes(ticket._id)) {
            const existingIds = [
              ...new Set((ticket.tags || []).map((t) => String(t._id || t))),
            ];
            const newIdsOrdered = getNewTagIdsFromFilter(
              ticket,
              tagsForApply,
              isMongoObjectId
            );
            const room = Math.max(0, MAX_TICKET_TAGS - existingIds.length);
            const idsToAppend = newIdsOrdered.slice(0, room);
            const mergedIds = [...existingIds];
            for (const id of idsToAppend) {
              if (!mergedIds.includes(id)) mergedIds.push(id);
            }
            return {
              ...ticket,
              ...updates,
              tags: mergedIds.map((id) => {
                return (
                  tagsForApply.find((t) => String(t._id || t) === id) ||
                  (ticket.tags || []).find((t) => String(t._id || t) === id) ||
                  { _id: id, name: id }
                );
              }),
            };
          }
          return ticket;
        })
      );

      const patchPayload = { ...updates };
      const hasNonTagPatch = Object.keys(patchPayload).length > 0;

      await Promise.all(
        selectedTickets.map(async (ticketId) => {
          const ticket = tickets.find((t) => t._id === ticketId);
          if (!ticket) return;

          const existingTagIds = new Set(
            (ticket.tags || []).map((t) => String(t._id || t))
          );
          const newIdsOrdered = getNewTagIdsFromFilter(
            ticket,
            tagsForApply,
            isMongoObjectId
          );
          const room = Math.max(0, MAX_TICKET_TAGS - existingTagIds.size);
          const tagIdsToAdd = newIdsOrdered.slice(0, room);

          const tasks = [];
          if (hasNonTagPatch) {
            tasks.push(
              dispatch(
                updateTicket({ ticketId, updateData: patchPayload })
              ).unwrap()
            );
          }
          if (tagIdsToAdd.length > 0) {
            tasks.push(
              dispatch(
                addTicketTagById({ ticketId, tags: tagIdsToAdd })
              ).unwrap()
            );
          }
          if (tasks.length > 0) {
            await Promise.all(tasks);
          }
        })
      );
      dispatch(getAllTickets(buildTicketListParams(activeTab, searchParams)));

      setStatus("");
      setPriority("");
      setAssignedAgent("");
      setTags([]);
      setSelected([]);
      toast.success("Updates applied successfully");
     
    } catch (error) {
      console.error("Failed to apply updates:", error);
      toast.error("Failed to apply updates");
      setLocalTickets(tickets);
    }
    finally {
      setIsApplying(false);
    }
  };
  const isFilterChanged =
    status !== "" ||
    priority !== "" ||
    assignedAgent !== "" ||
    tags.length > 0 ||
    (Array.isArray(dateRange) && dateRange[0] && dateRange[1]);
  const handleReset = () => {
    setStatus("");
    setPriority("");
    setAssignedAgent("");
    setTags([]);
    setDateRange([null, null]);
    setLocalTickets(tickets);
    toast.success("Filters reset successfully");
  };
  const handleDateChange = (dates, dateStrings) => {
    setDateRange(dates);
    // Remove setLocalTickets from here
    // Filtering will only happen on Apply
  };
  // const handleDateChange = (dates, dateStrings) => {
  //   setDateRange(dates)
  //   if (!dates) {
  //     setLocalTickets(tickets);
  //     return;
  //   }

  //   try {
  //     const [startDateRaw, endDateRaw] = dates;

  //     const startDate = new Date(startDateRaw);
  //     startDate.setHours(0, 0, 0, 0);

  //     const endDate = new Date(endDateRaw);
  //     endDate.setHours(23, 59, 59, 999);

  //     setLocalTickets(
  //       tickets.filter((ticket) => {
  //         const ticketDate = new Date(ticket.updated_at);
  //         return ticketDate >= startDate && ticketDate <= endDate;
  //       })
  //     );

  //     toast.success("Date filter applied successfully");
  //   } catch (error) {
  //     console.error("Failed to apply date filter:", error);
  //     toast.error("Failed to apply date filter");
  //     setLocalTickets(tickets);
  //   }
  // };

  const handleSearchChange = (e) => {
    e.stopPropagation(); // Prevent menu close
    setSearchMemberTerm(e.target.value);
  };
  if (numSelected === 0) {
    return null;
  }
  // const filteredMembers = teamMembers.filter(member => 
  //   member.full_name.toLowerCase().includes(searchTerm.toLowerCase()) || 
  //   (member.email && member.email.toLowerCase().includes(searchTerm.toLowerCase()))
  // );
  const selectStyle = {
    borderRadius: "8px",
    borderColor: '#D5DCE1',
    background: "#FFF",
    boxShadow: "0px 1px 2px 0px rgba(6, 21, 34, 0.05)",
    marginRight: "8px",
    fontSize: "14px",
    height: "32px",
    color: "#405261",
    "& .MuiSelect-icon": {
      fontSize: "16px",
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      right: "8px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    "& .MuiSelect-iconOpen": {
      fontSize: "16px",
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%) rotate(180deg)",
      right: "8px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
  };

  return (
    <Toolbar
      sx={{


        display: "flex",
        padding: "0 !important",
        width: '100%',
        flexDirection: 'row',
        justifyContent: "space-between",
        // minHeight: "72px !important",

      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", maxWidth: "100%" }}>
        <FormControl size="small" sx={{ ...selectStyle }}>
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            IconComponent={Dropdown}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) {
                return <span style={{
                  color: "#405261",
                  fontSize: "14px",
                  fontFamily: 'Inter',
                  fontWeight: 400,
                }}>Set status</span>;
              }
              return selected.charAt(0).toUpperCase() + selected.slice(1);
            }}
            sx={{
              height: "32px",
              width: "128px",
              fontSize: "14px",
              color: "#405261",
              "& .MuiSelect-select": {
                padding: "0px 12px",
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  border: "1px solid #D5DCE1",
                  marginTop: "4px",
                  width: "120px",
                  padding: ' 0px 8px',

                  borderRadius: "8px",
                  "& .MuiMenuItem-root": {
                    fontSize: "14px",
                    padding: "8px 8px",
                    color: "#405261",
                    "&:hover": {


                      color: '#061522',
                      borderRadius: "8px",
                      background: "#F9FAFA",
                    },
                    "&.Mui-selected": {
                      color: '#061522',
                      borderRadius: "8px",
                      background: "#F9FAFA",
                    },
                    "&.Mui-selected:hover": {
                      background: "#F9FAFA", // keep same on hover while selected
                    },
                  },
                },
              },
            }}

          >
            {["open", "pending", "solved", "spam"].map((option) => (
              <MenuItem key={option} value={option}>
                {option.charAt(0).toUpperCase() + option.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ ...selectStyle }}>
          <Select
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            IconComponent={Dropdown}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) {
                return <span style={{
                  color: "#405261",
                  fontSize: "14px",
                  fontFamily: 'Inter',
                  fontWeight: 400,
                }}>Set priority</span>;
              }
              const selectedOption = priorities.find(option => option.value === selected);
              if (selectedOption) {
                return (
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    whiteSpace: "nowrap"
                  }}>
                    <span style={{ fontSize: "14px" }}>
                      {selectedOption.label}
                    </span>
                  </div>
                );
              }
              return selected;
            }}
            sx={{
              height: "32px",
              width: priority ? "auto" : "128px",
              minWidth: "128px",
              maxWidth: "140px",
              fontSize: "14px",
              color: "#405261",
              transition: "width 0.2s ease",
              "& .MuiSelect-select": {
                padding: "0px 12px",
                display: "flex",
                alignItems: "center",
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  border: "1px solid #D5DCE1",
                  marginTop: "4px",
                  width: "184px",
                  padding: ' 0px 8px',
                  borderRadius: "8px",
                  "& .MuiMenuItem-root": {
                    fontSize: "14px",
                    padding: "8px 8px",
                    color: "#405261",
                    "&:hover": {


                      color: '#061522',
                      borderRadius: "8px",
                      background: "#F9FAFA",
                    },
                    "&.Mui-selected": {
                      color: '#061522',
                      borderRadius: "8px",
                      background: "#F9FAFA",
                    },
                    "&.Mui-selected:hover": {
                      background: "#F9FAFA", // keep same on hover while selected
                    },
                  },
                },
              },
            }}
          >
            {priorities.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <div style={{ display: "flex", alignItems: "center", gap: option.value === "low" ? "15px" : "8px" }}>
                  {option.value === "urgent" && <Urgent />}
                  {option.value === "high" && <High />}
                  {option.value === "medium" && <Medium />}
                  {option.value === "low" && <Low style={{ marginLeft: "5px" }} />}
                  &nbsp;{option.label}
                </div>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Space direction="vertical" style={{ ...selectStyle }}>
          <DatePicker.RangePicker
            className="custom-range-picker"
            onChange={handleDateChange}
            style={{
              width: "150px",
              height: "32px",
              borderRadius: "8px",
              background: "#FFF",

              padding: "8px 12px",
              boxShadow: "0px 1px 2px 0px rgba(6, 21, 34, 0.05)",
              border: "1px solid #D5DCE1",
            }}
            format="DD MMM"
            value={dateRange}
            placeholder={["From", "To"]}
          />
        </Space>
        <TeamMemberSelect
          value={assignedAgent}
          width="128px"
          dropDownTop="calc(100% + 5px)"
          wrapperStyle={{ marginRight: '8px' }}
          triggerStyles={{ height: '32px', maxWidth: '200px' }}
          dropdownWidth="280px"
          onChange={(e) => setAssignedAgent(e.target.value)}
          teamMembers={teamMembers}
        />


        {/* <Autocomplete
          ref={autocompleteRef}
          multiple
          id="tags-filled"
          options={availableTags}
          getOptionLabel={(option) => {
            return typeof option === "string" ? option : option.name;
          }}
          renderOption={(props, option) => (
            <li {...props}>
              <Box sx={{ 
                display: "flex", 
                alignItems: "center", 
                fontSize: "14px",
                color: "#405261",      
              }}>
                <ChatTag style={{ marginRight: "12px" }} />
                {typeof option === "string" ? option : option.name}
              </Box>
            </li>
          )}
          value={tags}
          onChange={(event, newValue) => {
            if (newValue.length > MAX_TICKET_TAGS) {
              toast.error(`You can only add up to ${MAX_TICKET_TAGS} tags.`);
              return;
            }
            handleTagChange(event, newValue);
          }}
          clearIcon={null}
          disableClearable
          freeSolo
          limitTags={3}
          size="small"
          sx={{
            minWidth: '106px',
            maxWidth: '200px',
            position: 'relative',
            '& .MuiOutlinedInput-root': {
              minWidth: '106px',
              maxWidth: '200px',
              maxHeight: '32px',
              display: 'flex',
              flexWrap: 'nowrap',
              overflowX: 'auto',
              overflowY: 'hidden',
              whiteSpace: 'nowrap',
              alignItems: 'center',
              transition: 'all 0.2s ease',
              border: '1px solid #d5dce1',
              paddingRight: '30px !important',
              scrollBehavior: 'smooth', // Add smooth scrolling
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              scrollbarWidth: 'none',
              '-ms-overflow-style': 'none',
            },
            '& .MuiAutocomplete-input': {
              marginLeft: '8px',
              padding: '0px 3px !important',
              minWidth: '30px', // Ensure input has minimum width
            },
            '& .MuiAutocomplete-endAdornment': {
              marginTop: '3px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },
            '& .MuiAutocomplete-popupIndicator': {
              padding: '2px',
              marginRight: '0px',
            },
          }}
          componentsProps={{
            popper: {
              sx: {
                '& .MuiAutocomplete-listbox': {
                  textAlign: 'center',
                },
                '& .MuiPaper-root': {
                  border: "1px solid #D5DCE1",
                  marginTop: "4px",
                  width: "184px",
                  borderRadius: "8px",
                }
              }
            }
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                variant="outlined"
                label={typeof option === "string" ? option : option.name}
                size="small"
                {...getTagProps({ index })}
                sx={{
                  height: "24px",
                  borderColor: "#d5dce1",
                  margin: "2px",
                  flexShrink: 0, // Prevent chips from shrinking
                }}
              />
            ))
          }
          renderInput={(params) => (
            <TextField 
              {...params} 
              InputProps={{
                ...params.InputProps,
                style: { color: "#405261", fontSize: "14px" },
                endAdornment: (
                  <>
                    {params.InputProps.endAdornment}
                    {tags.length === 0 && <Dropdown />}
                  </>
                )
              }}
              placeholder="Add tags" 
              sx={{
                "& .MuiInputBase-input::placeholder": {
                  fontSize: "14px",
                  color: "#405261",
                  opacity: 1,
                },
              }} 
            />
          )}
        /> */}
        <TicketTagInput
          formControlSx={{
            minWidth: "104px",

            maxWidth: "205px",
            width: "fit-content",
            boxSizing: "border-box",
          }}
          tags={tags}
          showHeader={false}
          setTags={setTags}
          maxTags={MAX_TICKET_TAGS}
          returnIds={true}
          sxHeight={32}
          dropDownTop="calc(100% + 5px)"
          chipsContainerSx={{
            minWidth: "106px",
            maxWidth: "230px",
            height: 32,
            display: "flex",
            alignItems: "center",
            overflowX: "auto",   // scroll horizontally if tags overflow
            gap: "2px",
            whiteSpace: "nowrap",
            boxSizing: "border-box",
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none",
          }}
        />

      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Button
          onClick={handleReset}
          sx={{
            width: "72px",
            height: "40px",
            flexShrink: 0,
            borderRadius: "8px",
            border: "1px solid #D5DCE1",
            background: "#FFF",
            boxShadow: "0px 1px 2px 0px rgba(6, 21, 34, 0.05)",
            color: "#405261",
            fontSize: "14px",
            fontWeight: 500,
            lineHeight: "18px",
            textTransform: "none",
            "&:not(.Mui-disabled):hover": {
              backgroundColor: "#FFFFFF",

              borderColor: "#C5CED6",
            },
            "&.Mui-disabled": {
              color: "#93A1B0",
              border: "1px solid #C5CED6",
              bgcolor: "#F9FAFA",
            },
          }}
        >
          Reset
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={isApplying || !isFilterChanged}
          sx={{
            height: "40px",
            borderRadius: "8px",
            width: "72px",
            background: "#148DFD",
            textTransform: "none",
            "&:not(.Mui-disabled):hover": {
              backgroundColor: "#0077CC",

            },
            "&.Mui-disabled": {

              color: "#93A1B0",
              border: "1px solid #C5CED6",
              bgcolor: "#F9FAFA",
            },


          }}

        >
          Apply
        </Button>
      </Box>
    </Toolbar>
  );
};

export default TicketFilterToolBar;









